import { useCallback, useState } from "react";

import {
  CreateRegistrationUpdateSubmissionDialog,
  RegistrationUpdateTarget,
  RegistrationUpdateWindow,
} from "./CreateRegistrationUpdateSubmissionDialog";

/** The row fields the dialog needs, common to all three grids' row types. */
export interface WasteStreamRowForUpdate {
  id: string;
  name: string;
  registration?: { id: string; stateRegisteredId?: string | null } | null;
}

/**
 * Maps a grid row and the facility it belongs to onto the dialog's target, so
 * the three grids share one spelling of it rather than each repeating which
 * row field feeds which prop.
 */
export function registrationUpdateTarget(
  row: WasteStreamRowForUpdate,
  facility: { id: string | null | undefined; name: string; state: string },
  window: RegistrationUpdateWindow
): RegistrationUpdateTarget {
  return {
    wasteStreamId: row.id,
    wasteStreamName: row.name,
    facilityId: facility.id,
    facilityName: facility.name,
    state: facility.state,
    registration: row.registration,
    window,
  };
}

/**
 * Shared by the three grids rendering `WasteStreamRegistrationCell`
 * (`TenantWasteStreams`, `WasteStreamsDataGrid`, `WasteStreamsDataGridV1`).
 * Owns the "Create update submission" dialog the "Update needed" chip opens,
 * so each grid only renders `updateSubmissionDialog` and wires the chip to
 * `openUpdateDialog`. The dialog creates the submission and hands a getter for
 * the id back here — synchronously, before it awaits anything — so opening it
 * is the caller's job, since each grid opens submissions differently
 * (`window.open` vs. `navigate`), and a grid opening a new tab needs that
 * synchronous handoff to reserve the tab inside the click's gesture.
 */
export function useWasteStreamRegistrationUpdateDialog(
  openSubmission: (getSubmissionId: () => Promise<string | undefined>) => void
) {
  const [target, setTarget] = useState<RegistrationUpdateTarget | null>(null);

  const openUpdateDialog = useCallback((next: RegistrationUpdateTarget) => setTarget(next), []);
  const closeUpdateDialog = useCallback(() => setTarget(null), []);

  const updateSubmissionDialog = target ? (
    <CreateRegistrationUpdateSubmissionDialog
      target={target}
      onClose={closeUpdateDialog}
      onCreated={openSubmission}
    />
  ) : null;

  return { openUpdateDialog, updateSubmissionDialog };
}
